import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { DecisionBadge } from './DecisionBadge';
import { ProfileImage } from './ProfileImage';
import { ScoringGuide } from './ScoringGuide';
import { useEvidenceImage } from './useEvidenceImage';
import type { EvidenceImageStatus } from './useEvidenceImage';
import { buildProfileUrl, formatNetwork, formatSigned, isLikelyUrl, toHref } from './formatters';
import { buildWhyExplanation, isImageUnavailable, labelForSignal } from './explanation';
import { HANDLE_MATCH_BASE_SCORE } from './scoringCriteria';
import type { UiCandidate, UiProfile, UiSignal } from './types';

const SectionLabel = ({ children }: { children: string }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
      }}
    >
      {children}
    </Typography>
  );
};

const ProfileCompareCard = ({ label, profile }: { label: string; profile: UiProfile | null }) => {
  const theme = useTheme();
  if (!profile) {
    return (
      <Stack
        spacing={0.5}
        sx={{
          flex: 1,
          backgroundColor: getSurfaceColor(theme, 'subtle'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.md}px`,
          p: 1.25,
        }}
      >
        <SectionLabel>{label}</SectionLabel>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: 13 }}>Not resolved</Typography>
      </Stack>
    );
  }

  const profileUrl = buildProfileUrl(profile.network, profile.handle);

  return (
    <Stack
      spacing={0.75}
      sx={{
        flex: 1,
        minWidth: 0,
        backgroundColor: getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.md}px`,
        p: 1.25,
      }}
    >
      <SectionLabel>{label}</SectionLabel>
      <Stack alignItems="center" direction="row" spacing={1}>
        <ProfileImage imageUrl={profile.imageUrl} displayName={profile.displayName} handle={profile.handle} size={40} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {profile.displayName ?? profile.handle ?? 'Unknown'}
          </Typography>
          {profile.handle && (
            <Typography noWrap sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>
              @{profile.handle} · {formatNetwork(profile.network)}
            </Typography>
          )}
        </Box>
      </Stack>
      {profileUrl && (
        <Link
          href={profileUrl}
          rel="noopener noreferrer"
          target="_blank"
          sx={{ alignItems: 'center', display: 'inline-flex', fontSize: 11.5, fontWeight: 600, gap: 0.3 }}
        >
          View profile <OpenInNewIcon sx={{ fontSize: 11 }} />
        </Link>
      )}
    </Stack>
  );
};

function resultLabel(signal: UiSignal): { text: string; color: 'success' | 'error' | 'muted' } {
  if (!signal.available) return { text: 'Not available', color: 'muted' };
  if (signal.polarity === 'supports') return { text: 'Supports', color: 'success' };
  if (signal.polarity === 'contradicts') return { text: 'Contradicts', color: 'error' };
  return { text: 'No signal', color: 'muted' };
}

const EvidenceTable = ({ signals }: { signals: UiSignal[] }) => {
  const muiTheme = useTheme();
  const rows = signals.filter(s => s.available || s.sourceValue || s.targetValue);
  if (rows.length === 0) return null;

  const renderValue = (value: string | null) => {
    if (!value) return '—';
    if (isLikelyUrl(value)) {
      return (
        <Link href={toHref(value)} rel="noopener noreferrer" target="_blank" sx={{ fontSize: 12.5 }}>
          {value} <OpenInNewIcon sx={{ fontSize: 10 }} />
        </Link>
      );
    }
    return value;
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Attribute</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Source</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Candidate</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Result</TableCell>
            <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700 }}>
              Contribution
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(signal => {
            const result = resultLabel(signal);
            const contributionColor =
              signal.contribution > 0
                ? muiTheme.palette.success.main
                : signal.contribution < 0
                  ? muiTheme.palette.error.main
                  : muiTheme.palette.text.secondary;
            const resultColor =
              result.color === 'success'
                ? muiTheme.palette.success.main
                : result.color === 'error'
                  ? muiTheme.palette.error.main
                  : muiTheme.palette.text.secondary;
            return (
              <TableRow key={signal.name}>
                <TableCell sx={{ fontSize: 12.5, fontWeight: 600 }}>{labelForSignal(signal.name)}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{renderValue(signal.sourceValue)}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{renderValue(signal.targetValue)}</TableCell>
                <TableCell sx={{ color: resultColor, fontSize: 12.5, fontWeight: 600 }}>{result.text}</TableCell>
                <TableCell align="right" sx={{ color: contributionColor, fontSize: 12.5, fontWeight: 700 }}>
                  {signal.contribution > 0 ? '+' : ''}
                  {signal.contribution.toFixed(2)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

const IMAGE_DECISION_COPY: Record<string, { label: string; explanation: string }> = {
  STRONG_MATCH: {
    label: 'Strong image match',
    explanation: 'Image evidence provides strong support that these profiles belong to the same identity.',
  },
  INCONCLUSIVE: {
    label: 'Inconclusive',
    explanation: 'The image comparison did not produce a strong enough signal to support or contradict the match.',
  },
  STRONG_MISMATCH: {
    label: 'Strong image mismatch',
    explanation: 'Image evidence indicates that the profiles are likely showing different people.',
  },
  LEGACY_STRONG_SIGNAL: {
    label: 'Strong image match',
    explanation: 'Image evidence provides strong support that these profiles belong to the same identity.',
  },
  LEGACY_NO_STRONG_SIGNAL: {
    label: 'Inconclusive',
    explanation: 'The image comparison did not produce a strong enough signal to support or contradict the match.',
  },
};

// Distinguishes a true 404 from a network/5xx/unexpected failure so the
// drawer's caption is accurate; 'loading'/'available' need no caption at all.
function evidenceImageLabel(status: EvidenceImageStatus): string | null {
  if (status === 'unavailable') return 'Analyzed image unavailable';
  if (status === 'error') return 'Could not load analyzed image';
  return null;
}

const ImageEvidenceSection = ({
  sourceProfile,
  candidate,
}: {
  sourceProfile: UiProfile;
  candidate: UiCandidate;
}) => {
  const theme = useTheme();
  const evidence = candidate.evidence;

  // Hooks must run unconditionally (before the early returns below), even
  // though their result is only used in the "available" branch. Each already
  // treats a null/undefined evaluationId as "no image" — see useEvidenceImage.
  const sourceImage = useEvidenceImage(evidence?.evaluationId, 'source');
  const targetImage = useEvidenceImage(evidence?.evaluationId, 'target');

  if (!evidence) return null;
  const { imageEvidence } = evidence;
  const unavailable = isImageUnavailable(imageEvidence.status);

  if (unavailable) {
    return (
      <Box
        sx={{
          backgroundColor: getSurfaceColor(theme, 'subtle'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.md}px`,
          p: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>Profile image unavailable</Typography>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12.5 }}>
          Image comparison could not be performed for this profile.
        </Typography>
      </Box>
    );
  }

  const copy = IMAGE_DECISION_COPY[imageEvidence.decision] ?? {
    label: 'Inconclusive',
    explanation: 'The image comparison did not produce a strong enough signal to support or contradict the match.',
  };

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <DecisionBadge decision={imageEvidence.decision} imageStatus={imageEvidence.status} />
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{copy.label}</Typography>
      </Stack>
      <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12.5 }}>{copy.explanation}</Typography>

      <Typography sx={{ color: theme.palette.text.secondary, fontSize: 11, textAlign: 'center' }}>
        Images used for this evaluation
      </Typography>
      <Stack alignItems="center" direction="row" justifyContent="center" spacing={2}>
        <Stack alignItems="center" spacing={0.5}>
          <ProfileImage
            imageUrl={sourceImage.objectUrl}
            displayName={sourceProfile.displayName}
            handle={sourceProfile.handle}
            size={64}
            genericFallback
          />
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>{formatNetwork(sourceProfile.network)}</Typography>
          {evidenceImageLabel(sourceImage.status) && (
            <Typography sx={{ fontSize: 10, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
              {evidenceImageLabel(sourceImage.status)}
            </Typography>
          )}
        </Stack>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: 16 }}>↔</Typography>
        <Stack alignItems="center" spacing={0.5}>
          <ProfileImage
            imageUrl={targetImage.objectUrl}
            displayName={candidate.profile?.displayName ?? null}
            handle={candidate.profile?.handle ?? null}
            size={64}
            genericFallback
          />
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
            {candidate.profile ? formatNetwork(candidate.profile.network) : '—'}
          </Typography>
          {evidenceImageLabel(targetImage.status) && (
            <Typography sx={{ fontSize: 10, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
              {evidenceImageLabel(targetImage.status)}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={3} justifyContent="center">
        <Typography sx={{ fontSize: 12.5 }}>
          Image score: {imageEvidence.imageScore != null ? imageEvidence.imageScore.toFixed(2) : '—'}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
          Score adjustment: {imageEvidence.imageAdjustment != null ? `${imageEvidence.imageAdjustment > 0 ? '+' : ''}${imageEvidence.imageAdjustment.toFixed(2)}` : '0.00'}
        </Typography>
      </Stack>
    </Stack>
  );
};

const TechnicalDetails = ({ candidate }: { candidate: UiCandidate }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const evidence = candidate.evidence;
  if (!evidence) return null;

  return (
    <Box>
      <Button
        onClick={() => setOpen(o => !o)}
        size="small"
        startIcon={open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        sx={{ color: theme.palette.text.secondary, fontSize: 11.5 }}
      >
        Technical details
      </Button>
      <Collapse in={open}>
        <Stack
          spacing={0.5}
          sx={{
            backgroundColor: getSurfaceColor(theme, 'subtle'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: `${radii.sm}px`,
            fontSize: 11.5,
            mt: 1,
            p: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 11.5 }}>Evaluation ID: {evidence.evaluationId}</Typography>
          <Typography sx={{ fontSize: 11.5 }}>Match type: {evidence.matchType}</Typography>
          {evidence.levenshteinDistance != null && (
            <Typography sx={{ fontSize: 11.5 }}>Levenshtein distance: {evidence.levenshteinDistance}</Typography>
          )}
          <Typography sx={{ fontSize: 11.5 }}>Image status: {evidence.imageEvidence.status ?? 'n/a'}</Typography>
          <Typography sx={{ fontSize: 11.5 }}>Raw classification/decision: {evidence.imageEvidence.decision}</Typography>
          {evidence.imageEvidence.reason && (
            <Typography sx={{ fontSize: 11.5 }}>Reason code: {evidence.imageEvidence.reason}</Typography>
          )}
          {evidence.imageEvidence.classificationVersion && (
            <Typography sx={{ fontSize: 11.5 }}>Classification version: {evidence.imageEvidence.classificationVersion}</Typography>
          )}
          <Typography sx={{ fontSize: 11.5 }}>Evaluated at: {new Date(evidence.evaluatedAt).toLocaleString()}</Typography>
        </Stack>
      </Collapse>
    </Box>
  );
};

export const EvidenceDrawer = ({
  open,
  onClose,
  sourceProfile,
  candidate,
}: {
  open: boolean;
  onClose: () => void;
  sourceProfile: UiProfile;
  candidate: UiCandidate | null;
}) => {
  const theme = useTheme();
  if (!candidate) return null;
  const { evidence } = candidate;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3 }}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Profile match evidence</Typography>
          <IconButton aria-label="Close evidence drawer" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12.5, mb: 2 }}>
          Understand why this profile was suggested and how the score was calculated.
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <ProfileCompareCard label="SOURCE PROFILE" profile={sourceProfile} />
          <ProfileCompareCard label="CANDIDATE PROFILE" profile={candidate.profile} />
        </Stack>

        <SectionLabel>WHY THIS CANDIDATE?</SectionLabel>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75, mb: 0.75 }}>
          <DecisionBadge decision={candidate.decision} imageStatus={evidence?.imageEvidence.status} />
        </Stack>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: 13, mb: 2.5 }}>
          {evidence ? buildWhyExplanation(evidence) : 'No evaluation is available yet for this candidate.'}
        </Typography>

        {evidence && (
          <>
            <Divider sx={{ borderColor: getSurfaceBorder(theme), mb: 2 }} />

            <SectionLabel>FINAL MATCH SCORE</SectionLabel>
            <Box
              sx={{
                backgroundColor: getSurfaceColor(theme, 'subtle'),
                border: `1px solid ${getSurfaceBorder(theme)}`,
                borderRadius: `${radii.md}px`,
                mt: 1,
                mb: 1.5,
                p: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 26, fontWeight: 700, textAlign: 'center', mb: 1 }}>
                {(evidence.combinedScore ?? evidence.metadataScore).toFixed(2)}
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary }}>
                    Handle similarity base
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    {HANDLE_MATCH_BASE_SCORE[evidence.matchType].toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary }}>
                    + Metadata signal contributions
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    {formatSigned(evidence.metadataScore - HANDLE_MATCH_BASE_SCORE[evidence.matchType])}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary }}>
                    + Image adjustment
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    {formatSigned(evidence.imageEvidence.imageAdjustment ?? 0)}
                  </Typography>
                </Stack>
                <Divider sx={{ borderColor: getSurfaceBorder(theme), my: 0.5 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>= Final match score</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                    {(evidence.combinedScore ?? evidence.metadataScore).toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <ScoringGuide evidence={evidence} />
            </Box>

            <Divider sx={{ borderColor: getSurfaceBorder(theme), mb: 2 }} />

            <SectionLabel>PROFILE EVIDENCE</SectionLabel>
            <Box sx={{ mt: 1, mb: 2.5 }}>
              <EvidenceTable signals={evidence.signals} />
            </Box>

            <Divider sx={{ borderColor: getSurfaceBorder(theme), mb: 2 }} />

            <SectionLabel>IMAGE EVIDENCE</SectionLabel>
            <Box sx={{ mt: 1, mb: 2 }}>
              <ImageEvidenceSection sourceProfile={sourceProfile} candidate={candidate} />
            </Box>

            <TechnicalDetails candidate={candidate} />
          </>
        )}
      </Box>
    </Drawer>
  );
};
